import React from 'react';

const PrivacyPolicy = () => {
  const lastUpdated = 'May 25, 2026';

  return (
    <div className="min-h-screen text-gray-300 font-sans">
      <div className="container mx-auto px-4 max-w-2xl py-8">
        <main>
          <h1 className="text-3xl font-bold text-white mb-6">Privacy Policy</h1>
          <p className="text-lg mb-4">
            <strong>Last Updated:</strong> {lastUpdated}
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">1. Who We Are</h2>
            <p className="mb-3">
              Forza is operated by Matteo Fabbri. If you have questions about this policy, contact us at
              matteo.fbr@gmail.com, or by mail at Carrer de Bergara, 7, 08002 Barcelona, Spain.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">2. Information We Collect</h2>
            <ul className="list-disc pl-5 mb-3 space-y-2">
              <li><strong>Account data:</strong> username, email, password hash, and authentication IDs (Google/Apple if used).</li>
              <li><strong>Profile data:</strong> fitness goals and preferences you enter during onboarding or settings.</li>
              <li><strong>HealthKit (optional):</strong> step count, sleep analysis, and active energy burned as daily aggregates. We read this data only with your permission.</li>
              <li><strong>Verification data:</strong> audio recordings, photos, generated or reported quest media references, and text you submit to verify quests, plus verification logs.</li>
              <li><strong>AI consent data:</strong> your AI data processing consent choice, consent version, and consent timestamp.</li>
              <li><strong>Usage data:</strong> app interactions, feature quota usage, rewarded-ad attempts, device and browser info, and IP address for security, analytics, fraud prevention, and operations.</li>
              <li><strong>Subscription and billing:</strong> iOS purchases are handled by Apple In-App Purchase and RevenueCat; we receive entitlement status, not full payment details.</li>
              <li><strong>Ads and consent:</strong> consent status, rewarded-ad attempts, reward status, and ad interaction metadata where provided by Google AdMob.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">3. How We Use Your Information</h2>
            <ul className="list-disc pl-5 mb-3 space-y-2">
              <li>Provide and maintain the Service, including generating quests, verifying completions, and operating AI Coach features.</li>
              <li>Sync HealthKit step count, sleep analysis, and active energy burned to verify eligible daily quests (if you opt in).</li>
              <li>Support account access, security, and fraud prevention.</li>
              <li>Synchronize Ultra entitlement status from RevenueCat for purchases, restores, renewals, cancellations, refunds, and billing events.</li>
              <li>Deliver optional rewarded ads and apply earned rewards, using consent-first personalized ads where legally allowed and non-personalized or limited ads otherwise.</li>
              <li>Improve the Service, monitor performance, investigate failures, and provide customer support.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">4. Health Data (HealthKit)</h2>
            <p className="mb-3">
              If you enable HealthKit, we read <strong>step count</strong>, <strong>sleep analysis</strong>, and <strong>active energy burned</strong> for the current day and store a daily snapshot
              with date, timezone, last sync time, and daily aggregate totals. We use this data only for quest verification and related features.
              We do not use HealthKit data for advertising, marketing, tracking, or AI provider prompts.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">5. Audio, Photos, and AI Features</h2>
            <p className="mb-3">
              Forza relies on AI and speech processing to generate personalized quests, provide AI Coach and Voice Coach,
              verify quest completion, check exercise form, and operate related AI-powered features. Before registration,
              you must provide explicit consent for this required AI data processing. If you do not consent, you cannot
              create an account because Forza cannot provide its core personalized service without this processing.
            </p>
            <p className="mb-3">
              Data sent to AI and speech providers may include fitness goals, preferences, profile context, onboarding
              answers, quest plans, quest activity, completion status, verification context, text submitted to AI features,
              voice recordings and transcripts, photos submitted for verification, exercise frames submitted for form
              checks, and app, device, and diagnostic context needed to operate, secure, and troubleshoot these features.
            </p>
            <p className="mb-3">
              We do not use audio, photos, HealthKit data, or sensitive fitness profile details for ad targeting.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">6. Sharing of Information</h2>
            <p className="mb-3">We share data only with service providers needed to operate the Service:</p>
            <ul className="list-disc pl-5 mb-3 space-y-2">
              <li><strong>Hosting and infrastructure:</strong> Render, MongoDB Atlas, Cloudflare R2.</li>
              <li><strong>Media delivery:</strong> ImageKit.</li>
              <li><strong>AI and speech providers:</strong> Google, Groq, OpenRouter, DeepSeek, OpenAI, and local/on-device providers where available.</li>
              <li><strong>Payments and subscriptions:</strong> Apple and RevenueCat for iOS Ultra purchase and entitlement synchronization.</li>
              <li><strong>Ads:</strong> Google AdMob for rewarded ads and reward attribution.</li>
              <li><strong>Monitoring:</strong> Better Stack if configured, and Render logs for production operations.</li>
            </ul>
            <p className="mb-3">We do not sell your personal data.</p>
            <p className="mb-3">
              We do not share your personal data for third-party advertising or profiling. Third-party service providers
              process data only as needed to operate Forza's services and must provide the same or equal protection for user
              data as described in this Privacy Policy and required by applicable App Store privacy guidelines.
            </p>
            <p className="mb-3">
              We may disclose information if required by law or to protect the rights and safety of users and the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">7. Ads and Tracking Choices</h2>
            <p className="mb-3">
              Rewarded ads are optional and are used to unlock limited feature credits. Where legally allowed and only with
              required consent or App Tracking Transparency permission, ads may be personalized. If consent or permission is
              denied, unavailable, or withdrawn, we use non-personalized or limited ads where supported. HealthKit data,
              audio, photos, and sensitive fitness profile data are not used for ad targeting.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">8. Data Retention</h2>
            <p className="mb-3">
              We retain data while your account is active. HealthKit snapshots are retained for up to 12 months. When you
              delete your account, we anonymize your account data and remove HealthKit snapshots. Anonymized quest history
              and verification logs may be retained for up to 12 months for audit and fraud prevention.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">9. Your Rights</h2>
            <ul className="list-disc pl-5 mb-3 space-y-2">
              <li>Access and correct your data in Settings.</li>
              <li>Download your data via the Settings page.</li>
              <li>Delete your account using the in-app deletion flow.</li>
              <li>Withdraw HealthKit permissions at any time in iOS Settings.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">10. International Transfers</h2>
            <p className="mb-3">
              Our servers and service providers may be located in the United States or other countries. By using the Service,
              you understand that your data may be processed outside your country of residence.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">11. Security</h2>
            <p className="mb-3">
              We use reasonable technical and organizational safeguards to protect your data. No system is completely secure,
              but we work to reduce risk.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">12. Contact</h2>
            <p className="mb-3">
              If you have questions about this Privacy Policy, contact us at matteo.fbr@gmail.com.
            </p>
          </section>
        </main>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
